<script lang="ts">
  import type { FieldProps } from '../Field/Field.types';

  // Note: the `trackBy` prop, the `single` prop, and the fact that options can be strings prevents us from knowing the type of the selected options

  type Option = any;

  type SelectedOptions = Option | Option[];

  export interface SelectProps extends FieldProps {
    /**
     * If there is only 1 option selected, it prevents that option from being de-selected
     */
    preventEmpty?: boolean;

    /**
     * @deprecated The `allowEmpty` prop is no longer supported; use `preventEmpty` instead.
     */
    allowEmpty?: string | boolean | null;

    /**
     * If `options` are an object, this is what prop to use for display.
     */
    displayBy?: string;

    /**
     * List of one or more fields to search on. When empty, displayBy will be used
     */
    searchBy?: string[];

    /**
     * Default field to track selected options by.
     */
    trackBy?: string;

    /**
     * Placeholder text.
     */
    placeholder?: string;

    /**
     * @deprecated Use the `error-text` prop instead
     */
    error?: string;

    /**
     * @deprecated Use the `hint-text` prop instead
     */
    hint?: string;

    /**
     * Sets a custom icon.
     */
    icon?: string | boolean;

    /**
     * Sets a name attribute on the native (hidden) select element
     *
     * Setting the default value to `undefined` so the attribute isn't rendered if not explicitly provided to ensure backwards compatibility with MP django forms
     */
    name?: string;

    /**
     * The list of all options to select from.
     */
    options?: Option[] | readonly Option[];

    /**
     * Lazily evaluate and set component `options`. Will execute upon user mouseover.
     */
    lazy?: () => Option[];

    /**
     * Sets the currently-selected value(s) for the component.
     * Accepts an array of Objects, or a single Object (if `single` is true), or a string
     */
    modelValue?: SelectedOptions;

    /**
     * @deprecated Use :model-value or v-model instead of :value.
     */
    value?: SelectedOptions | null;

    /**
     * Hides the search input
     */
    hideSearch?: boolean;

    /**
     * @deprecated The `searchable` prop is no longer supported; Use `hideSearch` instead.
     */
    searchable?: string | boolean | null;

    /**
     * If true, prevents the search term from being cleared when the drawer is dismissed.
     */
    preserveSearchTerm?: boolean;

    /**
     * Prevents the options from being filtered when the search term changes.
     * Allows the parent to filter the options.
     */
    disableFiltering?: boolean;

    /**
     * Functions as a single select, if true
     */
    single?: boolean;

    /**
     * Prevents the Selected Option from being truncated, if true
     */
    noTruncate?: boolean;

    /**
     * On an ajaxed request when the dropdown is open, we may be experiencing slower load times.
     * This flag will drop a loader into the dropdown.  Note: This has never been design reviewed.
     */
    loading?: boolean;

    /**
     * @deprecated Instead, use the `@search` event (the onSearch prop) and ensure its event handler returns a Promise that resolves after the search is complete.
     */
    searchLoading?: boolean;

    /**
     * In the selection text we use this to give more visual distinction to what type of item(s) are selected
     * E.g. 2 customers selected. An empty string will result in `2 selected`.
     */
    selectItemType?: string;

    /**
     * Hides the "check" icon if truthy
     */
    hideCheck?: boolean;

    searchPlaceholder?: string;

    /**
     * Equivalent to emitting a "search" event, except this prop can be used to populate the list of `options` using an HTTP request because a prop's return value can be received and awaited, unlike an event.
     *
     * **Tip:** to show a loading indicator while searching, return a Promise that resolves after the search is complete.
     *
     * **Warning:** the search input is debounced so there is no need to debounce this function.
     */
    onSearch?: (searchTerm: string) => Promise<void> | void;

    /**
     * Uses the "fuzzy search" algorithm when searching, if true
     */
    useFuzzySearch?: boolean;

    /**
     * Sets the placement of the dropdown
     * @default 'bottom-start'
     */
    menuPlacement?: Placement;

    /**
     * Enables teleporting the dropdown
     * @default false
     */
    enableTeleport?: boolean;

    /**
     * The selector or element to which the dropdown should be teleported
     * @default `'#stash-menus-mount-node'`
     */
    teleportTo?: string | HTMLElement;
  }
</script>

<script setup lang="ts">
  import { autoUpdate, flip, offset, type Placement, type Side, size, useFloating } from '@floating-ui/vue';
  import logger from '@leaflink/snitch';
  import { debounce, isEqual, isPlainObject } from 'es-toolkit';
  import { isEmpty, uniqueId } from 'es-toolkit/compat';
  import { computed, nextTick, onMounted, onUnmounted, type Ref, ref, useAttrs, watch } from 'vue';

  import useSearch from '../../composables/useSearch/useSearch';
  import { DEBOUNCE, KEY_CODES } from '../../constants';
  import vClickoutside from '../../directives/clickoutside/clickoutside';
  import { DEFAULT_MENUS_PLUGIN_NODE_ID } from '../../plugins/MenusPlugin';
  import Chip from '../Chip/Chip.vue';
  import Field from '../Field/Field.vue';
  import type { IconName } from '../Icon/Icon.vue';
  import Icon from '../Icon/Icon.vue';

  const GAP_HEIGHT = 6;

  const MENU_MAX_HEIGHT_PX = 300;

  defineOptions({
    name: 'll-select',
  });

  const props = withDefaults(defineProps<SelectProps>(), {
    preventEmpty: false,
    allowEmpty: null,
    displayBy: 'name',
    searchBy: () => [],
    trackBy: 'id',
    placeholder: 'Select option',
    error: '',
    hint: '',
    icon: 'caret-down',
    name: undefined,
    options: () => [],
    lazy: undefined,
    modelValue: () => [],
    value: null,
    hideSearch: false,
    searchable: null,
    preserveSearchTerm: false,
    disableFiltering: false,
    single: false,
    noTruncate: false,
    loading: false,
    searchLoading: false,
    selectItemType: '',
    hideCheck: false,
    searchPlaceholder: 'Search',
    onSearch: undefined,
    useFuzzySearch: false,
    menuPlacement: 'bottom-start',
    enableTeleport: false,
    teleportTo: `#${DEFAULT_MENUS_PLUGIN_NODE_ID}`,
  });

  const emit = defineEmits<{
    /**
     * Emitted when the model value changes.
     */
    (e: 'update:model-value', selectedOptions: SelectedOptions): void;
    /**
     * Emitted the selected value(s) a cleared.
     */
    (e: 'clear'): void;
    /**
     * Emitted when an option is added.
     */
    (e: 'add', optionAdded: Option): void;
    /**
     * Emitted when an option is removed.
     */
    (e: 'remove', optionRemoved: Option, index: number): void;
    /**
     * Emitted when the select is opened.
     */
    (e: 'opened'): void;
    /**
     * Emitted when the select is closed.
     */
    (e: 'closed', selectedOptions: SelectedOptions): void;
  }>();

  const attrs = useAttrs();
  const slots = defineSlots<{
    /**
     * Selected value(s) custom text. Exposes the option object and remove function.
     */
    selected(props: {
      /**
       * The selected option object.
       */
      option: Option;
      /**
       * Function to remove the selected option from the selection.
       */
      onRemove: () => void;
      /**
       * Class to apply to the selected chip.
       */
      chipSelectedClass: string;
    }): unknown;
    /**
     * Select custom option text. Exposes the option object
     */
    option(props: {
      /**
       * Option The option instance for this list item.
       */
      option: Option;
    }): unknown;
    /**
     * Slot for when there are no options available.
     */
    'no-options'(): unknown;
    /**
     * Slot to display a helpful hint message below the select field.
     */
    hint(): unknown;
  }>();

  const selectRef = ref<HTMLDivElement | null>(null);
  const contentRef = ref<HTMLDivElement | null>(null);
  const optionsWrapperRef = ref<HTMLDivElement | null>(null);
  const chipsRef = ref<HTMLUListElement | null>(null);
  const searchRef = ref<HTMLInputElement | null>(null);

  const chipsHeight = ref(0);
  const contentHeight = ref(0);
  const internalValue = ref<Option[]>([]) as Ref<Option[]>;
  const searchFor = ref<(searchTerm: string) => Option[]>(() => []); // initialized in the watch handler for options
  const searchTerm = ref('');
  const activeIndex = ref(-1);
  const isActive = ref(false);
  const pendingSearchRequests = ref({});
  const shouldUseLazyOptions = ref(false);

  const { floatingStyles, update: updateFloating } = useFloating(selectRef, optionsWrapperRef, {
    whileElementsMounted: autoUpdate,
    placement: props.menuPlacement,
    middleware: [
      flip(),
      offset(({ rects, placement }) => {
        // gets the current placement side, especially after flip() is run
        const placementSide = placement.split('-')[0] as Side;
        const isBottom = placementSide === ('bottom' satisfies Side);

        // if the placement is at the bottom, we need to adjust the offset
        if (isBottom && chipsHeight.value > contentHeight.value) {
          return chipsHeight.value - rects.reference.height + GAP_HEIGHT;
        } else {
          return GAP_HEIGHT;
        }
      }),
      size({
        apply({ availableHeight, elements, rects }) {
          if (props.enableTeleport) {
            Object.assign(elements.floating.style, {
              maxWidth: `${rects.reference.width}px`,
              maxHeight: `${Math.min(availableHeight, MENU_MAX_HEIGHT_PX)}px`,
            });
          }
        },
      }),
    ],
  });
  // ResizeObserver is a browser API and is undefined in Node (SSR). Only create the observer when available
  // so this component remains SSR-safe (e.g. VitePress, Nuxt). Use optional chaining where we observe/disconnect.
  const resizeObserverChips =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => debouncedUpdateChipsHeight()) : null;

  const isSearchable = computed(() => !props.hideSearch && props.searchable !== 'false' && props.searchable !== false);

  const internalOptions = computed(() => {
    let OPTIONS = (shouldUseLazyOptions.value && props.lazy ? props.lazy() : props.options).filter(Boolean); // prevent [undefined]

    // if `options` is an array... but not an array of objects
    if (!isPlainObject(OPTIONS[0])) {
      OPTIONS = OPTIONS.map((name, id) => ({ name, id }) as unknown as Option);
    }

    return OPTIONS;
  });

  const readOnlyValue = computed(() => {
    if (!internalValue.value.length) {
      return '';
    }

    return internalValue.value.map((option) => option[props.displayBy] || option).join(', ');
  });

  const isSearching = computed(() => props.searchLoading || Object.keys(pendingSearchRequests.value).length > 0);

  const filteredOptions = computed(() => {
    if (props.disableFiltering || !(searchTerm.value || '').trim()) {
      return internalOptions.value;
    }

    return searchFor.value(searchTerm.value);
  });

  const isChipsOneLine = computed(() => chipsHeight.value <= contentHeight.value);

  const shouldShowTotal = computed(
    () => !isActive.value && !props.single && !isChipsOneLine.value && internalValue.value.length,
  );

  const clearSelectionText = computed(() => {
    return [internalValue.value.length, props.selectItemType, 'selected'].filter(Boolean).join(' ');
  });

  const shouldPreventEmpty = computed(() => {
    return props.preventEmpty || props.allowEmpty === 'false' || props.allowEmpty === false;
  });

  watch(searchTerm, (currentValue, oldValue) => oldValue !== currentValue && setIndex(-1));

  watch(internalValue, () => debouncedUpdateChipsHeight(), { deep: true });

  watch(
    () => props.modelValue,
    (value) => {
      // multiple
      if (Array.isArray(value)) {
        const newValue = value.filter(Boolean); // sometimes modelValue is [undefined]

        if (!isEqual(newValue, internalValue.value)) {
          internalValue.value = newValue;
        }

        return;
      }

      // single; if value is a non-empty string or a non-empty object
      if (!isEmpty(value)) {
        if (!isEqual(value, internalValue.value[0])) {
          internalValue.value[0] = value;
        }

        return;
      }

      // if value is an empty object or falsy
      internalValue.value = [];
    },
    { immediate: true },
  );

  watch(
    () => props.options,
    () => {
      const { searchFor: searchForFn } = useSearch<Option>({
        items: computed(() => internalOptions.value),
        fieldNames: props.searchBy.length ? props.searchBy : [props.displayBy],
        trackBy: props.trackBy,
      });

      searchFor.value = (searchTerm) => searchForFn(searchTerm, { fuzzy: props.useFuzzySearch });
    },
    { immediate: true },
  );

  watch(chipsHeight, () => debouncedUpdateFloating());

  const debouncedUpdateFloating = debounce(() => updateFloating(), 50);
  const debouncedUpdateChipsHeight = debounce(() => updateChipsHeight(), 50);

  const isOptionDisabled = (option: Option) => {
    return option && typeof option === 'object' && 'disabled' in option && option?.disabled;
  };

  const clear = () => {
    internalValue.value = [];
    emit('update:model-value', props.single ? undefined : internalValue.value);
    emit('clear');
  };

  /**
   * Adds (or selects) an option.
   * @param {Option} option The selected option
   */
  const handleSelect = (option?: Option) => {
    if (props.disabled || !option || isOptionDisabled(option)) {
      return;
    }

    if (!isSelected(option)) {
      if (props.single) {
        internalValue.value = [];
      }

      internalValue.value.push(option);
      emit('update:model-value', props.single ? internalValue.value[0] : internalValue.value);
      emit('add', option);
    } else {
      remove(option);
    }

    if (props.single) {
      dismiss();
    }
  };

  const handleSelectChange = (evt) => {
    // If this is a single select, just pass along the option
    // This short-circuit is purely for readability, as the iterations for multi would also work for single selects
    if (props.single) {
      const selectedOption = internalOptions.value.find((opt) => opt[props.trackBy]?.toString() === evt.target.value);
      handleSelect(selectedOption);
      return;
    }

    // Spread the HTMLCollection (https://developer.mozilla.org/en-US/docs/Web/API/HTMLCollection) to array so we can invoke `find`
    const selectedOptions = [...evt.target.selectedOptions];

    // If it's a multiselect, the HTML select used for the screen reader can have multiple items
    // deselected and a new one selected in one change event.
    // We need to reconcile the new selected options with our internal state

    // 1. remove all the items in our internal state that are no longer present
    // (Spread this array so we can remove items from it while iterating)
    for (const internalOption of [...internalValue.value]) {
      const selectedOption = selectedOptions.find((opt) => opt.value === internalOption[props.trackBy]?.toString());

      if (!selectedOption) {
        remove(internalOption);
      }
    }

    // 2. add all the items that are now selected if needed
    for (const selectedOption of selectedOptions) {
      const internalOption = internalOptions.value.find(
        (opt) => opt[props.trackBy]?.toString() === selectedOption.value,
      );

      if (isSelected(internalOption)) {
        continue;
      } else {
        handleSelect(internalOption);
      }
    }
  };

  /**
   * Removes (or un-selects) an option.
   * @param {Option} option The option to be removed
   */
  const remove = (option: Option) => {
    if (props.disabled || (shouldPreventEmpty.value && internalValue.value.length === 1)) {
      return;
    }

    const index = internalValue.value.findIndex((opt) => opt[props.trackBy] === option[props.trackBy]);

    if (index === -1) {
      logger.warn('ll-select: could not find option to remove', option);
      return;
    }

    internalValue.value.splice(index, 1);
    emit('update:model-value', props.single ? internalValue.value[0] : internalValue.value);
    emit('remove', option, index);
  };

  /**
   * Opens the `options` drawer and provides focus.
   */
  const open = () => {
    if (isActive.value) {
      return;
    }

    debouncedUpdateChipsHeight();

    isActive.value = true;
    emit('opened');

    if (isSearchable.value) {
      nextTick(() => {
        searchRef.value?.focus({ preventScroll: true });
      });
    }
  };

  /**
   * Closes the drawer and cleans up a few settings.
   */
  const dismiss = () => {
    if (isActive.value) {
      emit('closed', internalValue.value);
    }

    setIndex(-1);
    isActive.value = false;

    if (!props.preserveSearchTerm) {
      searchTerm.value = '';
    }

    // The optional chaining here is required.
    // There are cases when the component is removed from the DOM and the ref is lost.
    // .ie when the component is used in a modal and the modal is dismissed.
    // Tracked in this ticket: F-1893
    contentRef.value?.blur(); // if the component remains in focus, clicking it again will not open the options list
  };

  /**
   * Scroll list items when up and down keys are pressed
   * @param {KeyboardEvent} e The native keydown event.
   */
  const scrollItems = async (e: KeyboardEvent) => {
    await nextTick();

    if (!contentRef.value) {
      return;
    }

    const allowedKeyCodes = [KEY_CODES.ENTER, KEY_CODES.ESCAPE] as number[];

    // Wait for highlighted item update after using keyboard (`onKeyDown`)
    if (!allowedKeyCodes.includes(e.keyCode)) {
      const el = contentRef.value.querySelector('.stash-select__option--highlighted');
      const elBCR = el?.getBoundingClientRect();
      const parentBCR = contentRef.value.getBoundingClientRect();

      if (!elBCR || !parentBCR) {
        return;
      }

      if (elBCR.bottom >= parentBCR.bottom || elBCR.top <= parentBCR.top + elBCR.height) {
        el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
  };

  /**
   * Sets the `activeIndex` on hover, so that the user may then fine-tune
   * their selection with the arrow keys.
   */
  const setIndex = (index: number) => {
    activeIndex.value = index;
  };

  /**
   * Handles all user keyboard input on the Dropdown.
   * @param {KeyboardEvent} e The native keydown event.
   */
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === KEY_CODES.ESCAPE) {
      dismiss();
    } else if (e.keyCode === KEY_CODES.DOWN && activeIndex.value < filteredOptions.value.length - 1 && isActive.value) {
      activeIndex.value++;
    } else if (e.keyCode === KEY_CODES.UP && activeIndex.value > 0) {
      if (isActive.value) {
        activeIndex.value--;
      }

      // Scroll to top when search is available and first element is highlighted (Using 100 to be sure that it'll be sure that search is visible)
      if (optionsWrapperRef.value && isSearchable.value && activeIndex.value === 0) {
        optionsWrapperRef.value.scrollTop -= 100;
      }
    } else if (e.keyCode === KEY_CODES.ENTER && activeIndex.value !== -1 && isActive.value) {
      handleSelect(filteredOptions.value[activeIndex.value]);
    } else {
      return;
    }

    e.preventDefault(); // if keyDown did something / we didn't return
    scrollItems(e);
  };

  const handleSearchInput = debounce(function () {
    search();
  }, DEBOUNCE.FAST);

  const search = async function () {
    if (!props.onSearch) {
      return;
    }

    const requestId = uniqueId('search-request-');

    try {
      pendingSearchRequests.value[requestId] = true;
      await props.onSearch(searchTerm.value);
    } finally {
      delete pendingSearchRequests.value[requestId];
    }
  };

  /**
   * Dynamically sets classes on an `option` in the drawer.
   * @param {Option} option The `option` for which to generate classes
   * @param {number} index The index of the `option` in the list of `options`
   */
  const optionClass = (option: Option, index: number) => {
    return [
      {
        'stash-select__option': true,
        'stash-select__option--disabled': isOptionDisabled(option),
        'stash-select__option--highlighted': activeIndex.value === index,
        'stash-select__option--selected': isSelected(option),
      },

      // @deprecated
      // backwards compatibility
      {
        'is-selected': activeIndex.value === index,
        'bg-ice-200': activeIndex.value === index,
        'bg-blue-100 text-ice-700': isSelected(option),
      },
    ];
  };

  /**
   * Finds out if the given item is already present in the selected options
   * @param {Option} option The item to check
   * @returns {boolean} Returns true if element is selected
   */
  const isSelected = (option?: Option | null): boolean => {
    if (option === undefined || option === null) {
      return false;
    }

    return internalValue.value.some((value) => value?.[props.trackBy] === option[props.trackBy]);
  };

  /**
   * Checks if the element is within the Select menu by checking both the content and the teleportTo element
   */
  function isElementWithinSelectMenu(element: Node | null) {
    if (!element) {
      return false;
    }

    const isElementInMenu = contentRef.value?.contains(element);
    const isElementInTeleportedMenu = optionsWrapperRef.value?.contains(element);

    return isElementInMenu || isElementInTeleportedMenu;
  }

  function handleFocusOut(focusEvent: FocusEvent) {
    const nextFocusedElement = focusEvent.relatedTarget as Node | null;
    const isFocusedElementInMenu = isElementWithinSelectMenu(nextFocusedElement);

    if (!isFocusedElementInMenu && nextFocusedElement) {
      dismiss();
    }
  }

  function handleOutsideClick(event: MouseEvent) {
    const target = event.target as Node | null;
    const isFocusedElementInMenu = isElementWithinSelectMenu(target);

    if (!isFocusedElementInMenu) {
      dismiss();
    }
  }

  /**
   * Handles the mouse leave of the select and clears item highlight
   */
  const handleMouseLeave = () => {
    setIndex(-1);
  };

  /**
   * Update height of chips container
   */
  const updateChipsHeight = async () => {
    // Wait for the DOM to update so we can get an accurate value for chips' clientHeight
    await nextTick();

    if (chipsRef.value && contentRef.value) {
      contentHeight.value = contentHeight.value === 0 ? contentRef.value.clientHeight : contentHeight.value;
      chipsHeight.value = chipsRef.value.clientHeight;
    }
  };

  onMounted(() => {
    if (attrs.onInput) {
      throw new Error('ll-select: use the @update:model-value event instead of @input.');
    }

    if (resizeObserverChips && chipsRef.value) {
      resizeObserverChips.observe(chipsRef.value); // no-op when null (SSR)
    }

    window.addEventListener('resize', debouncedUpdateChipsHeight);

    debouncedUpdateChipsHeight();
  });

  /**
   * Remove event listener to handle component width when resizing window
   */
  onUnmounted(() => {
    window.removeEventListener('resize', updateChipsHeight);
    resizeObserverChips?.disconnect(); // null during SSR
    debouncedUpdateChipsHeight.cancel();
  });
</script>

<template>
  <Field
    v-bind="props"
    class="input ll-select stash-select relative"
    data-test="stash-select"
    :class="attrs.class"
    :error-text="props.errorText || props.error"
    :hint-text="props.hintText || props.hint"
  >
    <template v-if="props.isReadOnly" #default="{ fieldId, labelId }">
      <div class="flex h-input items-center text-sm">
        <span :id="fieldId" :aria-labelledby="labelId" class="show-empty h-min">
          {{ readOnlyValue }}
        </span>
      </div>
    </template>
    <template v-else #default="{ fieldId, fieldErrorId, hasError }">
      <!-- SCREEN READER ONLY -->
      <select
        :id="fieldId"
        :aria-errormessage="fieldErrorId"
        :aria-invalid="hasError"
        class="sr-only"
        :disabled="props.disabled"
        :multiple="!props.single"
        :name="props.name"
        @change="handleSelectChange"
      >
        <option
          v-for="(option, index) in filteredOptions"
          :key="`srOnlyOption-${index}`"
          :selected="isSelected(option)"
          :value="option[props.trackBy]"
          :disabled="isOptionDisabled(option)"
        >
          {{ option[props.displayBy] || '' }}
        </option>

        <!-- Empty option to allow deselecting non-multiselects -->
        <option value=""></option>
      </select>

      <!-- SELECT -->
      <div
        ref="selectRef"
        v-clickoutside="handleOutsideClick"
        role="listbox"
        aria-hidden="true"
        class="stash-select__content-wrapper"
        :aria-controls="'listbox-' + fieldId"
        :aria-expanded="isActive"
        :aria-label="props.placeholder"
        :aria-disabled="props.disabled || undefined"
        :class="[
          {
            'stash-select--disabled': !!props.disabled,
            'stash-select--error': !!(props.errorText || props.error),
            'stash-select--active': isActive,
            'stash-select--single': props.single,
          },

          // @deprecated
          // backwards compatibility
          {
            'is-active': isActive,
            'is-single': props.single,
            'is-disabled': props.disabled,
          },
        ]"
        @keydown="onKeyDown"
        @keyup.esc="dismiss"
      >
        <!-- CONTENT -->
        <div
          :id="'listbox-' + fieldId"
          ref="contentRef"
          class="input-field stash-select__content"
          tabindex="0"
          data-test="stash-select|dropdown-trigger"
          @focusin="open"
          @focusin.once="shouldUseLazyOptions = true"
          @focusout="handleFocusOut"
          @keydown="open"
          @mouseleave="handleMouseLeave"
        >
          <!-- CHIPS -->
          <ul ref="chipsRef" class="stash-select__chips">
            <!-- PLACEHOLDER -->
            <li
              v-if="!internalValue.length"
              class="stash-select__placeholder mr-0 pl-1.5"
              :class="{ truncate: !props.noTruncate }"
            >
              {{ props.placeholder }}
            </li>

            <template v-if="props.single">
              <li
                v-for="option of internalValue"
                :key="`chip-${option[props.trackBy]}`"
                class="stash-select__selected mr-0 pl-1.5"
                :class="{ truncate: !props.noTruncate }"
              >
                <slot
                  name="selected"
                  :option="option"
                  :on-remove="() => remove(option)"
                  chip-selected-class="stash-select__chip"
                >
                  {{ option[props.displayBy] || option }}
                  <button
                    tabindex="-1"
                    class="stash-select__remove"
                    @keypress.enter.prevent="remove(option)"
                    @mousedown.prevent.stop="remove(option)"
                  >
                    <Icon icon="close" name="close" size="small" />
                  </button>
                </slot>
              </li>
            </template>

            <template v-else>
              <li v-for="option of internalValue" :key="`chip-${option[props.trackBy]}`" class="inline-block">
                <slot
                  name="selected"
                  :option="option"
                  :on-remove="() => remove(option)"
                  chip-selected-class="stash-select__chip"
                >
                  <Chip
                    bg-color="blue-500"
                    is-removable
                    text-color="white"
                    class="stash-select__chip"
                    @remove="remove(option)"
                  >
                    {{ option[props.displayBy] || option }}
                  </Chip>
                </slot>
              </li>
            </template>
          </ul>

          <!-- TOTAL -->
          <div v-if="shouldShowTotal" class="stash-select__total">
            <Chip bg-color="blue-500" is-removable text-color="white" class="stash-select__chip" @remove="clear">
              {{ clearSelectionText }}
            </Chip>
          </div>

          <!-- TOGGLE -->
          <Icon
            v-if="props.icon"
            class="stash-select__icon"
            data-test="stash-select|toggle-icon"
            :name="props.icon as IconName"
            :class="{ 'text-ice-500': props.disabled }"
            @mousedown.prevent="isActive && dismiss()"
          />
          <!-- DRAWER -->
          <Teleport :to="props.teleportTo" :disabled="!props.enableTeleport">
            <transition name="fade">
              <!-- Duplicate stash-select class so :global(:where(.stash-select) …) rules still apply when the menu is teleported outside the Field root. -->
              <div
                v-show="isActive && !props.disabled"
                ref="optionsWrapperRef"
                class="stash-select stash-select__border-selector w-full shadow-2xl"
                :style="floatingStyles"
                @click.stop
              >
                <!-- SEARCH -->
                <div v-if="isSearchable" class="flex items-center border-b border-blue-500 pr-1.5">
                  <input
                    ref="searchRef"
                    v-model="searchTerm"
                    type="text"
                    autocomplete="off"
                    class="stash-select__search"
                    :data-test="attrs['data-test'] ? attrs['data-test'] + '-search' : 'stash-select|search'"
                    :placeholder="props.searchPlaceholder"
                    :spellcheck="false"
                    @input="handleSearchInput"
                  />
                  <Icon name="search" class="text-ice-500" />
                </div>

                <!-- OPTIONS -->
                <ul class="stash-select__options options my-1.5 w-full border-white bg-white">
                  <li
                    v-for="(option, index) in filteredOptions"
                    :key="`option-${option[props.trackBy]}`"
                    :data-test="option[props.trackBy]"
                    :class="optionClass(option, index)"
                    @click="handleSelect(option)"
                    @mouseenter.self="setIndex(index)"
                  >
                    <slot name="option" :option="option">
                      {{ option[props.displayBy] || option }}
                    </slot>

                    <span v-if="isSelected(option) && !props.hideCheck" class="ml-auto pl-1.5">
                      <Icon class="text-blue-500" name="check" />
                    </span>
                  </li>
                  <li v-show="props.loading || isSearching" class="m-1.5 cursor-default p-1.5">
                    <Icon data-test="stash-select|options-loading" name="working" class="animate-spin text-ice-500" />
                  </li>
                  <li
                    v-show="!props.loading && !isSearching && !filteredOptions.length"
                    class="m-1.5 cursor-default p-1.5"
                    data-test="stash-select|no-options"
                  >
                    <slot name="no-options"> No options </slot>
                  </li>
                </ul>
              </div>
            </transition>
          </Teleport>
        </div>
      </div>
    </template>
    <template v-if="slots.hint?.()" #hint>
      <slot name="hint"></slot>
    </template>
  </Field>
</template>

<style module>
  @reference "../../../styles/main.css";

  :global(:where(.stash-select) .stash-select__content),
  :global(:where(.stash-select) .stash-select__content-wrapper) {
    height: var(--height-input);
  }

  :global(:where(.stash-select) .stash-select__content) {
    cursor: pointer;
    overflow: hidden;
    position: absolute;
    background: var(--color-white);
    border: 1px solid var(--color-ice-500);
    border-radius: var(--radius-sm);
    color: var(--color-ice-700);
    display: block;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    font-weight: var(--stash-font-weight-normal);
    outline: none;
    padding: 0 !important;
    width: 100%;
  }

  :global(:where(.stash-select) .stash-select__content:hover) {
    border-color: var(--color-ice-500);
  }

  :global(:where(.stash-select) .stash-select__content:focus-within) {
    border-color: var(--color-blue-500) !important;
  }

  :global(:where(.stash-select) .stash-select--disabled .stash-select__content) {
    background-color: var(--color-ice-100);
    border-color: var(--color-ice-500);
    color: var(--color-ice-500);
    pointer-events: none !important;
  }

  :global(:where(.stash-select) .stash-select--active .stash-select__content) {
    cursor: default;
    height: auto;
    min-width: var(--width-select-menu);
    overflow: visible;
    z-index: var(--z-index-control);
    box-shadow: var(--shadow-sm);
  }

  :global(
    :where(.stash-select) .stash-select--error :where(.stash-select__content, .stash-select__content:hover:not(:focus))
  ) {
    border-color: var(--color-red-500) !important;
  }

  :global(:where(.stash-select) .stash-select__placeholder) {
    color: var(--color-ice-500);
  }

  :global(:where(.stash-select) .stash-select--active .stash-select__placeholder) {
    cursor: default;
  }

  :global(:where(.stash-select) .stash-select__icon) {
    color: var(--color-ice-700);
    cursor: pointer;
    pointer-events: none;
    position: absolute;
    right: --spacing(1.5);
    top: --spacing(1.5);
  }

  :global(:where(.stash-select) .stash-select--disabled .stash-select__icon) {
    color: var(--color-ice-500);
  }

  :global(:where(.stash-select) .stash-select--active .stash-select__icon) {
    pointer-events: auto;
  }

  :global(:where(.stash-select) .stash-select__chips) {
    /* distribute (or split?) the margin between chip and chips container. TODO? */
    --half-space: calc(--spacing(1.5) / 2);

    padding: var(--half-space);
    padding-right: --spacing(9);
  }

  /* chip AND placeholder AND selected: */
  :global(:where(.stash-select) .stash-select__chips > li) {
    cursor: pointer;
    height: var(--height-chip);
    margin: var(--half-space);
    max-width: 100%;
  }

  :global(:where(.stash-select) .stash-select--disabled .stash-select__chips > li) {
    cursor: default;
  }

  :global(:where(.stash-select) .stash-select__content-wrapper .stash-select__chip) {
    border-radius: var(--radius-sm);
    font-size: var(--text-sm);
    font-weight: var(--stash-font-weight-normal);
    height: var(--height-chip);
    max-width: inherit;
    text-transform: none;
  }

  :global(:where(.stash-select) .stash-select--disabled .stash-select__chip) {
    background: var(--color-ice-500);
    color: var(--color-white);
  }

  /* Base total chip styles moved above the more specific descendant rule to satisfy no-descending-specificity */
  :global(:where(.stash-select) .stash-select__total) {
    display: block;
    font-weight: var(--stash-font-weight-semibold);
    line-height: var(--line-height-button);
    position: absolute;
    right: --spacing(6);
    white-space: nowrap;
    z-index: var(--z-index-content);

    /* -- design TBD --- */
    top: 0;
    left: 0;
    bottom: 0;
    color: var(--color-white);
    background: white;
    padding: --spacing(1.5);
  }

  :global(:where(.stash-select) .stash-select__content-wrapper .stash-select__total .stash-select__chip) {
    font-weight: var(--stash-font-weight-normal);
  }

  :global(:where(.stash-select) .stash-select__selected) {
    color: var(--color-ice-700);
  }

  :global(:where(.stash-select) .stash-select--disabled .stash-select__selected) {
    color: var(--color-ice-500);
  }

  :global(:where(.stash-select) .stash-select__selected :where(.stash-select__remove)) {
    display: none;
  }

  :global(:where(.stash-select) .stash-select__search) {
    position: relative;
    width: 100%;
    height: var(--height-input);
    background: var(--color-white);
    border-radius: var(--radius-sm);
    color: var(--color-ice-700);
    display: block;
    font-family: var(--font-sans);
    font-size: var(--text-sm);
    font-weight: var(--stash-font-weight-normal);
    outline: none;
    padding: 0 --spacing(3);
    border: 0 !important;
  }

  /* Include compound selector so teleported menu (same node carries .stash-select + __border-selector) still matches */
  :global(:where(.stash-select) .stash-select__border-selector),
  :global(:where(.stash-select).stash-select__border-selector) {
    background: white;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-blue-500);
    max-height: var(--max-height-select-menu);
    overflow: auto;
    z-index: var(--z-index-dialog);
  }

  :global(:where(.stash-select) .stash-select__option) {
    align-items: center;
    border-radius: var(--radius-sm);
    cursor: pointer;
    display: flex;
    line-height: --spacing(6);
    margin: --spacing(1.5);
    padding: --spacing(1.5);
  }

  :global(:where(.stash-select) .stash-select__option--disabled) {
    color: var(--color-ice-500);
    cursor: not-allowed;
  }

  :global(:where(.stash-select) .stash-select__option--highlighted) {
    background-color: var(--color-ice-200);
  }

  :global(:where(.stash-select) .stash-select__option--selected) {
    background-color: var(--color-blue-100);
    color: var(--color-ice-700);
  }
</style>
